import Card from "components/Card/Card";
import type { OptionType } from "components/inputs";
import {
  DATE_TO_STRING,
  DatePicker,
  Select,
  STRING_TO_DATE,
  TextInput,
} from "components/inputs";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { setValidationState } from "reducers/main";
import {
  useGetClaimByIdQuery,
  useGetClaimValidationsQuery,
  usePatchClaimByIdMutation,
} from "services/claims-api";
import { setCurrentClaim } from "services/claimsDataSlice.ts";
import {
  useGetPoliciesByRreIdQuery,
  useGetTinsByRreIdQuery,
} from "services/rre-api.ts";
import { useToken } from "utils/context";
import { DATE_FORMAT } from "utils/date-utils";
import { useDispatch, useSelector } from "utils/redux";

import ClaimsPageContainer from "./ClaimsPageContainer";

const ClaimPlanInformation = () => {
  const { claimId } = useParams<{ claimId: string }>();
  const { accessToken } = useToken();
  const dispatch = useDispatch();
  const [patchClaimById] = usePatchClaimByIdMutation();
  const [selectedTin, setSelectedTin] = useState<number | undefined>(null);

  const {
    control,
    reset,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useForm(); // Default for claimant selector);

  const { data: claim, isLoading } = useGetClaimByIdQuery(
    { id: claimId },
    { skip: false },
  );

  const { data: claimValidation } = useGetClaimValidationsQuery(
    { id: claimId },
    { skip: !claimId },
  );

  const rreId = claim && claim.rre.toString();
  const { data: rrePolicies } = useGetPoliciesByRreIdQuery(
    { rreId },
    { skip: !rreId },
  );

  const { data: rreTins } = useGetTinsByRreIdQuery({ rreId }, { skip: !rreId });

  useEffect(() => {
    if (!isLoading) {
      dispatch(setCurrentClaim(claim));
      // dispatch(setClaimsFormData(claim));
      reset(claim);
    }
  }, [dispatch, isLoading]);

  useEffect(() => {
    if (claimValidation) {
      dispatch(
        setValidationState({
          errors: claimValidation.filter((e) => e.level === "ERROR"),
          warnings: claimValidation.filter((e) => e.level === "WARN"),
        }),
      );
    }
  }, [claimValidation, dispatch]);

  const handleSave = useCallback(
    async (field: string, value: unknown) => {
      try {
        await patchClaimById({
          id: claimId,
          data: field === "" ? value : { [field]: value },
          useDetail: true,
        })
          .unwrap()
          .then((result) => {
            dispatch(setCurrentClaim(result));
          });
      } catch (error) {
        console.error("Save failed:", error);
      }
    },
    [patchClaimById, accessToken, claimId],
  );

  const handleSaveBatch = useCallback(
    async (data) => {
      await patchClaimById({
        id: claimId,
        data: data,
        useDetail: true,
      })
        .unwrap()
        .then((result) => {
          dispatch(setCurrentClaim(result));
        })
        .catch((error) => {
          console.error("Save failed:", error);
          return false;
        });
    },
    [patchClaimById, accessToken, claimId],
  );

  useEffect(() => {
    if (selectedTin) {
      const rreTin = rreTins?.results?.find((tin) => tin.id === selectedTin);
      handleSaveBatch({
        tin: rreTin?.tin,
        office_code: rreTin?.office_code,
      });
      setValue("tin", rreTin?.tin);
      setValue("office_code", rreTin?.office_code);
    }
  }, [selectedTin]);

  const constants = useSelector((state) => state.shared.constants);

  const policyOptions = rrePolicies?.results?.map((policy) => ({
    label: policy.name,
    value: policy.id,
  }));

  const tinOptions = rreTins?.results?.map((tin) => ({
    label: `TIN=${tin.tin} / Office Code=${tin.office_code}`,
    value: tin.id,
  }));

  const focusedField = useSelector((state) => state.main.focusedField);
  const preDefinedPolicy = watch("predefined_policy");

  const updatePolicyFields = useCallback(
    async (preDefinedPolicy) => {
      const policy = rrePolicies?.results?.find(
        (pol) => preDefinedPolicy === pol.id,
      );
      if (!policy) return;

      const policyUpdateFields = [
        "plan_insurance_type",
        "is_self_insured",
        "self_insured_type",
        "policyholder_first_name",
        "policyholder_last_name",
        "legal_name",
        "dba_name",
        "exhaust_date_for_dollar_limit_for_no_fault_insurance",
        "no_fault_insurance_limit",
        "policy_number",
        "plan_contact_first_name",
        "plan_contact_last_name",
        "plan_contact_department_name",
        "plan_contact_phone",
        "plan_contact_phone_extension",
        "tin",
        "office_code",
      ];

      const updatePayload = Object.fromEntries(
        policyUpdateFields.map((field) => [field, policy[field]]),
      );

      // handleSaveBatch(updatePayload);
      updatePayload["predefined_policy"] = preDefinedPolicy;
      Object.keys(updatePayload).forEach((key) => {
        setValue(key, updatePayload[key]);
      });
      handleSaveBatch(updatePayload);
    },
    [claim, rrePolicies, setValue],
  );

  return (
    <ClaimsPageContainer>
      <div key={`claimant-form-plan-information ${preDefinedPolicy}`}>
        <div className="grid-layout gl-2 no-gap-tb">
          <div>
            <Card heading="Choose Policy" cardNumber={1}>
              <Controller
                control={control}
                key={"predefined_policy"}
                name={"predefined_policy"}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    label="Select Pre-defined Policy"
                    name={name}
                    errors={errors}
                    isFocused={focusedField === name}
                    options={policyOptions}
                    value={policyOptions?.find(
                      (option: OptionType) => option.value === value,
                    )}
                    onChange={(o: OptionType) => {
                      onChange(o.value);
                      updatePolicyFields(o.value);
                    }}
                  />
                )}
              />
            </Card>

            <Card
              heading="Enter Policy & Policy Holder Information"
              cardNumber={2}
            >
              <Controller
                control={control}
                key={"plan_insurance_type"}
                name={"plan_insurance_type"}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    label="Select Type"
                    name={name}
                    errors={errors}
                    isFocused={focusedField === name}
                    options={constants?.PLAN_INSURANCE_TYPE || []}
                    value={constants?.PLAN_INSURANCE_TYPE?.find(
                      (option: OptionType) => option.value === value,
                    )}
                    onChange={(o: OptionType) => {
                      onChange(o.value);
                      handleSave("plan_insurance_type", o.value);
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                key={"is_self_insured"}
                name={"is_self_insured"}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    label="Self insured?"
                    name={name}
                    errors={errors}
                    isFocused={focusedField === name}
                    options={constants?.TRUE_FALSE_CHOICES || []}
                    value={constants?.TRUE_FALSE_CHOICES?.find(
                      (option: OptionType) => option.value === value,
                    )}
                    onChange={(o: OptionType) => {
                      onChange(o.value);
                      handleSave("is_self_insured", o.value);
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                key={"self_insured_type"}
                name={"self_insured_type"}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    label="Type"
                    name={name}
                    errors={errors}
                    isFocused={focusedField === name}
                    options={constants?.SELF_INSURED_TYPE || []}
                    value={constants?.SELF_INSURED_TYPE?.find(
                      (option: OptionType) => option.value === value,
                    )}
                    onChange={(o: OptionType) => {
                      onChange(o.value);
                      handleSave("self_insured_type", o.value);
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                key={"policyholder_first_name"}
                name={"policyholder_first_name"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    // readOnly={
                    //                       getValues("self_insured_type") ===
                    //                         SELF_INSURED_TYPE.OTHER &&
                    //                       !getValues("policyholder_first_name")
                    //                     }
                    label="First Name"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "policyholder_first_name",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                key={"policyholder_last_name"}
                name={"policyholder_last_name"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    // readOnly={getValues("self_insured_type") ===
                    //   SELF_INSURED_TYPE.OTHER &&
                    // !getValues("policyholder_last_name")}
                    label="Last Name"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "policyholder_last_name",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                key={"legal_name"}
                name={"legal_name"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    // readOnly={getValues("self_insured_type") ===
                    //   SELF_INSURED_TYPE.INDIVIDUAL &&
                    // !getValues("legal_name")}
                    label="Legal Name"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave("legal_name", getValues(field.name))
                    }
                  />
                )}
              />
              <Controller
                control={control}
                key={"dba_name"}
                name={"dba_name"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    // readOnly={getValues("self_insured_type") ===
                    //   SELF_INSURED_TYPE.INDIVIDUAL && !getValues("dba_name")}
                    label="DBA Name"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() => handleSave("dba_name", getValues(field.name))}
                  />
                )}
              />
              <Controller
                key={"exhaust_date_for_dollar_limit_for_no_fault_insurance"}
                name="exhaust_date_for_dollar_limit_for_no_fault_insurance"
                control={control}
                render={({ field: { value, name, onChange } }) => (
                  <DatePicker
                    name={name}
                    label="No Fault Exhaust Date"
                    initialDate={STRING_TO_DATE(value)}
                    // readOnly={[
                    //   PLAN_INSURANCE_TYPE.WORKERS_COMP,
                    //   PLAN_INSURANCE_TYPE.LIABILITY,
                    // ].indexOf(getValues("plan_insurance_type")) !== -1 &&
                    // !getValues(
                    //   "exhaust_date_for_dollar_limit_for_no_fault_insurance",
                    // )}
                    onDateChange={(date) => {
                      onChange(date ? format(date, DATE_FORMAT) : null);
                      const stringDate = DATE_TO_STRING(date);
                      handleSave(
                        "exhaust_date_for_dollar_limit_for_no_fault_insurance",
                        stringDate,
                      );
                    }}
                    allowFutureDates={true}
                    errors={errors}
                    isFocused={focusedField === name}
                  />
                )}
              />
              <Controller
                control={control}
                name={"no_fault_insurance_limit"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    // readOnly={
                    //   [
                    //     PLAN_INSURANCE_TYPE.WORKERS_COMP,
                    //     PLAN_INSURANCE_TYPE.WORKERS_COMP,
                    //   ].indexOf(getValues("plan_insurance_type")) !== -1 &&
                    //   !getValues("no_fault_insurance_limit")
                    // }
                    placeholder={"0.00"}
                    label="No Fault Insurance Amount ($)"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "no_fault_insurance_limit",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                name={"policy_number"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    placeholder={"X".repeat(30)}
                    label="Policy Number"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave("policy_number", getValues(field.name))
                    }
                  />
                )}
              />
            </Card>
          </div>
          <div>
            <Card heading="Plan Contact Information" cardNumber={3}>
              <Controller
                control={control}
                key={"plan_contact_first_name"}
                name={"plan_contact_first_name"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="First Name"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "plan_contact_first_name",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                key={"plan_contact_last_name"}
                name={"plan_contact_last_name"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Last Name"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "plan_contact_last_name",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                key={"plan_contact_department_name"}
                name={"plan_contact_department_name"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Department"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "plan_contact_department_name",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
              <Controller
                control={control}
                key={"plan_contact_phone"}
                name={"plan_contact_phone"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Phone"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave("plan_contact_phone", getValues(field.name))
                    }
                  />
                )}
              />
              <Controller
                control={control}
                key={"plan_contact_phone_extension"}
                name={"plan_contact_phone_extension"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="Phone + Extension"
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave(
                        "plan_contact_phone_extension",
                        getValues(field.name),
                      )
                    }
                  />
                )}
              />
            </Card>

            <Card heading="TIN Information" cardNumber={4}>
              <Controller
                control={control}
                key={"rre_tin"}
                name={"rre_tin"}
                render={({ field: { name, value, onChange } }) => (
                  <Select
                    label="Select TIN"
                    name={name}
                    errors={errors}
                    isFocused={focusedField === name}
                    options={tinOptions}
                    value={tinOptions?.find(
                      (option: OptionType) => option.value === value,
                    )}
                    onChange={(o: OptionType) => {
                      onChange(o.value);
                      setSelectedTin(o?.value as number);
                    }}
                  />
                )}
              />
              <Controller
                control={control}
                key={"tin"}
                name={"tin"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="RRE TIN"
                    placeholder={"X".repeat(9)}
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() => handleSave("tin", getValues(field.name))}
                  />
                )}
              />
              <Controller
                control={control}
                key={"office_code"}
                name={"office_code"}
                render={({ field }) => (
                  <TextInput
                    {...field}
                    label="RRE Office Code"
                    placeholder={"X".repeat(9)}
                    errors={errors}
                    isFocused={focusedField === field.name}
                    onBlur={() =>
                      handleSave("office_code", getValues(field.name))
                    }
                  />
                )}
              />
            </Card>
          </div>
        </div>
      </div>
    </ClaimsPageContainer>
  );
};

export default ClaimPlanInformation;
